// src/scripts/useAgentsLocal.js

import { useState, useEffect, useMemo } from "react";
import agentsData from "./agents.json";

/* ────────────────────────────────────────────────────────────────────────── */
/*                         AgentMeta Section                                */
/*  Generates and persists stable placeholder metadata for each agent.      */
/* ────────────────────────────────────────────────────────────────────────── */

// generate one-off random integers
function randomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function genMetaDefaults() {
  const viewCount   = randomInt(100, 2000);
  const dailyViews  = Array.from({ length: 7 }, () => randomInt(0, viewCount / 7));
  const reviewCount = randomInt(0, 300);
  const ratingAvg   = parseFloat((Math.random() * 2 + 3).toFixed(1)); // 3.0–5.0
  const voteCount   = randomInt(reviewCount, reviewCount * 5);
  const composite   = viewCount * 0.6 + voteCount * 0.3 + ratingAvg * 100 * 0.1;

  return {
    createdAt:      new Date(Date.now() - randomInt(0, 30) * 86400000).toISOString(),
    viewCount,
    dailyViews,
    reviewCount,
    ratingAvg,
    voteCount,
    compositeScore: composite,
    featured:       false,
    featuredOrder:  0,
    sponsored:      false,
    transactions:   0,
  };
}

// Load the entire map from localStorage (or return an empty object)
function loadMap() {
  try {
    return JSON.parse(localStorage.getItem("agentMetaMap")) || {};
  } catch {
    return {};
  }
}

function saveMap(map) {
  localStorage.setItem("agentMetaMap", JSON.stringify(map));
}

/**
 * Returns a stable metadata object for `id`. If none exists,
 * generate defaults and persist them in localStorage.
 */
export function getMeta(id) {
  const map = loadMap();
  if (!map[id]) {
    map[id] = genMetaDefaults();
    saveMap(map);
  }
  return map[id];
}

/**
 * Increment a field in the stored metadata and re-persist.
 * Call this on user actions (e.g., views, votes, etc.).
 */
export function bumpMeta(id, field, by = 1) {
  const map = loadMap();
  if (!map[id]) {
    map[id] = genMetaDefaults();
  }
  map[id][field] = (map[id][field] || 0) + by;
  saveMap(map);
  return map[id];
}

/* ────────────────────────────────────────────────────────────────────────── */
/*                      useAgentsLocal & Normalization                       */
/*  Loads raw agent data, merges in stable placeholder metadata,             */
/*  and provides hooks to bump view/vote counts.                             */
/* ────────────────────────────────────────────────────────────────────────── */

/**
 * Normalize a raw agent object by merging in stable placeholder metadata.
 */
export function normalise(raw) {
  const base = {
    id:           raw.slug || raw.agent_name,
    name:         raw.agent_name,
    description:  raw.tagline || "",
    industry:     raw.industry || "",
    category:     raw.category || "",
    logo:         raw.logo_url || "",
    url:          raw.url || "",
    website_url:  raw.website_url || "",
    buzz:         raw.buzz || "",
    overview:     raw.overview || "",
    key_features: raw.key_features || [],
    use_cases:    raw.use_cases || [],
  };

  // Pull in stable placeholders
  const meta = getMeta(base.id);

  return {
    ...base,
    createdAt:      raw.createdAt      || meta.createdAt,
    viewCount:      (raw.viewCount      != null ? raw.viewCount      : meta.viewCount),
    dailyViews:     raw.dailyViews     && raw.dailyViews.length === 7
                       ? raw.dailyViews
                       : meta.dailyViews,
    reviewCount:    (raw.reviewCount    != null ? raw.reviewCount    : meta.reviewCount),
    ratingAvg:      (raw.ratingAvg      != null ? raw.ratingAvg      : meta.ratingAvg),
    voteCount:      (raw.voteCount      != null ? raw.voteCount      : meta.voteCount),
    compositeScore: (raw.compositeScore  != null ? raw.compositeScore : meta.compositeScore),
    featured:       (raw.featured        != null ? raw.featured        : meta.featured),
    featuredOrder:  (raw.featuredOrder   != null ? raw.featuredOrder   : meta.featuredOrder),
    sponsored:      (raw.sponsored       != null ? raw.sponsored       : meta.sponsored),
    transactions:   (raw.transactions    != null ? raw.transactions    : meta.transactions),
  };
}

/**
 * Custom hook to load and normalize all agents from a local JSON file,
 * merging in stable placeholders via the above getMeta logic.
 */
export function useAgentsLocal() {
  const [rawAgents, setRawAgents] = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    // Instantly load the bundled JSON data
    setRawAgents(agentsData);
    setLoading(false);
  }, []);

  // Memoize normalized list so that it only recomputes when rawAgents changes
  const agents = useMemo(() => rawAgents.map(normalise), [rawAgents]);

  return { agents, loading };
}

/**
 * Helper to bump viewCount in localStorage and normalized state.
 * Call bumpAgentView(id) whenever you want +1 to viewCount.
 */
export function bumpAgentView(agentId) {
  bumpMeta(agentId, "viewCount", 1);
}

/**
 * Helper to bump voteCount in localStorage and normalized state.
 * Call bumpAgentVote(id) whenever you want +1 to voteCount.
 */
export function bumpAgentVote(agentId) {
  bumpMeta(agentId, "voteCount", 1);
}
